/**
 * tableScroller.ts - Scrolls the table and page, and schedules work to run once a scroll settles.
 * Private to `createTableInteractions`. It knows nothing about drags or editing: callers pass what to
 * run when a scroll settles.
 */

import $ from 'jquery';

import * as TableLayoutConstants from 'notebook/tables/constants/TableLayoutConstants';
import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import type {TableDom} from 'notebook/tables/store/logic/interactions/tableDom';
import type {TableModel} from 'notebook/tables/store/models/TableModel';
import type {TableProps} from 'notebook/tables/store/TableStore';

/** How long a programmatic scroll animates for, and so how long a retry waits for one to settle. */
export const DRAG_SCROLL_DURATION = 200;
const DRAG_SCROLL_PADDING = 30;

export interface TableScroller {
  /** Animates the table's scroll containers by the given offsets, calling `onSettled` when each finishes. */
  scrollBy: (offsetLeft: number, offsetTop: number, onSettled: () => void) => void;
  /** Scrolls the table and page so the cell at `index` is visible below the sticky header. */
  scrollCellIntoView: (index: TableIndex, onSettled: () => void) => void;
  /** Scrolls the page to center the table when it is entirely out of view. */
  scrollTableIntoView: () => void;
  /** Runs `callback` after `delay`, unless the table has unmounted or the work is cancelled. */
  schedule: (callback: () => void, delay: number) => void;
  /** Cancels everything scheduled. Called when the table unmounts. */
  cancelPendingWork: () => void;
}

export function createTableScroller({
  dom,
  getTableModel,
  getTableProps,
  isMounted,
}: {
  dom: TableDom;
  getTableModel: () => TableModel;
  getTableProps: () => TableProps;
  isMounted: () => boolean;
}): TableScroller {
  const {elements} = dom;
  const pendingTimeouts = new Set<ReturnType<typeof setTimeout>>();

  const schedule = (callback: () => void, delay: number): void => {
    const timeout = setTimeout(() => {
      pendingTimeouts.delete(timeout);
      if (isMounted()) {
        callback();
      }
    }, delay);
    pendingTimeouts.add(timeout);
  };

  const scrollBy = (offsetLeft: number, offsetTop: number, onSettled: () => void): void => {
    if (!isMounted() || (offsetLeft === 0 && offsetTop === 0)) {
      return;
    }
    const horizontalScrollContainer = elements.horizontalScrollContainer;
    const verticalScrollContainer = dom.getTableVerticalScrollContainer(getTableModel().views);
    const animationOptions = {complete: onSettled, duration: DRAG_SCROLL_DURATION};

    // If they're the same container, then scroll together
    if (horizontalScrollContainer === verticalScrollContainer && horizontalScrollContainer) {
      $(horizontalScrollContainer)
        .stop()
        .animate(
          {
            scrollLeft: horizontalScrollContainer.scrollLeft + offsetLeft,
            scrollTop: horizontalScrollContainer.scrollTop + offsetTop,
          },
          animationOptions
        );
      return;
    }
    if (horizontalScrollContainer) {
      $(horizontalScrollContainer)
        .stop()
        .animate({scrollLeft: horizontalScrollContainer.scrollLeft + offsetLeft}, animationOptions);
    }
    if (verticalScrollContainer) {
      $(verticalScrollContainer)
        .stop()
        .animate({scrollTop: verticalScrollContainer.scrollTop + offsetTop}, animationOptions);
    }
  };

  const scrollCellIntoView = ([i, j]: TableIndex, onSettled: () => void): void => {
    if (!isMounted()) {
      return;
    }
    const tableHorizontalScrollArea = elements.horizontalScrollContainer;
    const tableHorizontalScrollAreaBox = tableHorizontalScrollArea?.getBoundingClientRect() ?? null;
    const pageVerticalScrollAreaBox = elements.pageVerticalScrollContainer?.getBoundingClientRect() ?? null;
    const stickyHeader = elements.stickyHeader;
    const stickyHeaderBox = stickyHeader?.getBoundingClientRect() ?? null;

    if (!tableHorizontalScrollAreaBox || !pageVerticalScrollAreaBox) {
      return;
    }

    const {visibility, views, singleInputs, measurements, affixed} = getTableModel();
    const virtualizationHandle = elements.virtualizationHandle;
    if (virtualizationHandle && tableHorizontalScrollArea) {
      virtualizationHandle.scrollIntoView({
        index: i,
        calculateViewLocation: ({
          itemTop,
          itemBottom,
          viewportTop,
          viewportBottom,
          locationParams: {behavior, align, ...rest},
        }) => {
          const {isStickyHeaderVisible} = visibility;
          const {isMaximizedOrSplitScreen} = views;
          const singleInputGroupHeight = dom.getSingleInputGroupHeight();
          const {stickyTopContentHeight} = measurements;

          /** A buffer of 1 base row-height to to trigger scroll for */
          const bufferHeight = TableLayoutConstants.TABLE_ROW_HEIGHT;

          /** The offset from the top to trigger scrolling from */
          let topOffset = 0;
          // don't add buffer for the first row
          if (i !== 0) {
            topOffset += bufferHeight;
          }
          if (isStickyHeaderVisible) {
            topOffset += stickyTopContentHeight;
          } else if (isMaximizedOrSplitScreen) {
            topOffset -= singleInputGroupHeight;
          }

          /** The offset from the bottom to trigger scrolling from */
          let bottomOffset = 0;
          // don't add buffer for the last row
          if (i !== getTableProps().table.table.length - 1) {
            bottomOffset += bufferHeight;
          }
          // Maximized tables include Single Inputs in the scroll area
          if (isMaximizedOrSplitScreen) {
            bottomOffset += singleInputGroupHeight;
          } else {
            bottomOffset += TableLayoutConstants.TABLE_FOOTER_HEIGHT;
            if (dom.showsPersistentHorizontalScrollbar()) {
              bottomOffset += TableLayoutConstants.NATIVE_SCROLLBAR_HEIGHT_ESTIMATE;
            }
          }

          // Single Inputs should be stuck to the top of the window
          const isSingleInput = j < (singleInputs.boundaryIndex ?? 0);
          if (
            isSingleInput &&
            (isStickyHeaderVisible ||
              itemTop - singleInputGroupHeight < viewportTop ||
              itemTop - singleInputGroupHeight > viewportBottom)
          ) {
            return {
              ...rest,
              behavior,
              align: align ?? 'start',
              offset: -(singleInputGroupHeight + stickyTopContentHeight),
            };
          }
          if (itemTop < viewportTop + topOffset) {
            return {...rest, behavior, align: align ?? 'start', offset: -topOffset};
          }
          if (itemBottom > viewportBottom - bottomOffset) {
            return {...rest, behavior, align: align ?? 'end', offset: bottomOffset};
          }
          return null;
        },
      });
    }

    const cellBox = dom.getScrollAreaCellRect([i, j]);
    // There is an edge case with inserting/deleting columns and rows where the selection updates out of band
    // with the table updating. Therefore, we make a best effort to perform the scroll
    if (cellBox == null) {
      return;
    }

    // Compute change in scrollLeft to horizontally scroll desired cell into view
    // If there are affixed columns, we want to scroll the cell into view relative to the affixed columns
    let tableBoxLeft = stickyHeaderBox ? stickyHeaderBox.left : tableHorizontalScrollAreaBox.left;

    // Don't scroll the current cell if it is affixed
    const cellIsAffixed = j < affixed.affixedColumnCount && affixed.showAffixedShadow;
    if (!cellIsAffixed) {
      if (stickyHeader) {
        // Not full screen
        // If there is a sticky header, then we want to scroll the cell into view relative to the rightmost
        // cell in the sticky header
        const lastAffixedHeaderCell = $(stickyHeader).find('.cell-rightShadow');
        if (lastAffixedHeaderCell.length) {
          tableBoxLeft = lastAffixedHeaderCell[0].getBoundingClientRect().right;
        }
      } else if (tableHorizontalScrollArea) {
        // Full screen
        // If there is no sticky header, then we want to scroll
        // to the rightmost cell that is sticky
        const lastAffixedColumnCell = $(tableHorizontalScrollArea).find(
          'tbody tr:first-child td.cell-rightShadow'
        );
        if (lastAffixedColumnCell.length) {
          tableBoxLeft = lastAffixedColumnCell[0].getBoundingClientRect().right;
        }
      }
    }

    let offsetLeft = 0;
    if (cellBox.left < tableBoxLeft) {
      offsetLeft = cellBox.left - tableBoxLeft - DRAG_SCROLL_PADDING;
    } else if (cellBox.right > tableHorizontalScrollAreaBox.right) {
      offsetLeft = cellBox.right - tableHorizontalScrollAreaBox.right + DRAG_SCROLL_PADDING;
    }

    // Compute change in scrollTop to vertically scroll desired cell into view
    // If there is a sticky header, we want to scroll the cell into view relative to the sticky header
    let offsetTop = 0;
    const bottomOfHeader = stickyHeaderBox?.bottom ?? pageVerticalScrollAreaBox.top;
    const bottomOfScroll = pageVerticalScrollAreaBox.bottom;
    const stickyFooterSize = dom.getStickyFooterSize();
    const visibleViewHeight = bottomOfScroll - stickyFooterSize - bottomOfHeader;
    const cellTallerThanView = cellBox.height > visibleViewHeight;
    const cellTopAboveHeader = cellBox.top < bottomOfHeader;
    const cellBottomBelowFooter = cellBox.bottom > bottomOfScroll - stickyFooterSize;
    if (cellTopAboveHeader && !cellTallerThanView) {
      offsetTop = cellBox.top - bottomOfHeader - DRAG_SCROLL_PADDING;
    } else if (cellBottomBelowFooter && !cellTallerThanView) {
      offsetTop = cellBox.bottom - bottomOfScroll + stickyFooterSize + DRAG_SCROLL_PADDING;
    }

    scrollBy(offsetLeft, offsetTop, onSettled);
  };

  const scrollTableIntoView = (): void => {
    const horizontalScrollAreaBox = elements.horizontalScrollContainer?.getBoundingClientRect();
    const verticalScrollArea = elements.pageVerticalScrollContainer;
    const verticalScrollAreaBox = verticalScrollArea?.getBoundingClientRect();
    if (!horizontalScrollAreaBox || !verticalScrollAreaBox) {
      return;
    }

    // If the table has been scrolled out of view, scroll it back centered
    if (
      horizontalScrollAreaBox.bottom < verticalScrollAreaBox.top ||
      horizontalScrollAreaBox.top > verticalScrollAreaBox.bottom
    ) {
      const verticalCenter = (verticalScrollAreaBox.top + verticalScrollAreaBox.bottom) / 2;
      const tableCenter = (horizontalScrollAreaBox.top + horizontalScrollAreaBox.bottom) / 2;
      verticalScrollArea?.scrollBy?.({top: tableCenter - verticalCenter, behavior: 'auto'});
    }
  };

  return {
    scrollBy,
    scrollCellIntoView,
    scrollTableIntoView,
    schedule,
    cancelPendingWork: () => {
      for (const timeout of pendingTimeouts) {
        clearTimeout(timeout);
      }
      pendingTimeouts.clear();
    },
  };
}
